import React, { useState, useEffect, useRef } from 'react';

const InteractiveGridPage = ({ page, onNext }) => {
  const [selectedCell, setSelectedCell] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [answers, setAnswers] = useState([]); 
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const timerRef = useRef(null);
  const audioRefs = useRef({});
  const questionAudioRef = useRef(null);

  const questions = page.questions || (page.audioList?.files || []).map((audio, index) => ({
    id: `q-${index}`,
    audio: audio,
    correctCellIds: []
  }));

  const currentQuestion = questions[currentQuestionIndex];

  
  const getOrderedCells = () => {
    if (!page.grid.cells || !page.grid.cellOrder || page.grid.cellOrder !== 'column-major') {
      return page.grid.cells || [];
    }
    
    const cells = page.grid.cells;
    const rows = page.grid.rows || 1;
    const columns = page.grid.columns || 1;
    const orderedCells = new Array(cells.length);
    
   
    cells.forEach((cell, index) => {
      const row = Math.floor(index / columns);
      const col = index % columns;
      const newIndex = col * rows + row;
      orderedCells[newIndex] = cell;
    });
    
    return orderedCells;
  };

  const orderedCells = getOrderedCells();

  
  useEffect(() => {
    if (page.grid.mode === 'quiz' && isStarted && currentQuestion && currentQuestionIndex < questions.length) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      setSelectedCell(null);
      setTimeRemaining(null);
      setIsPlaying(false);

      if (currentQuestion.audio && questionAudioRef.current) {
        questionAudioRef.current.src = currentQuestion.audio.url;
        questionAudioRef.current.play().catch(err => {
          
          startTimer();
        });
        setIsPlaying(true);
        
        questionAudioRef.current.onended = () => {
          setIsPlaying(false);
          startTimer();
        };
      } else {
        startTimer();
      }
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isStarted, currentQuestionIndex, page]);

  const startTimer = () => {
    if (page.config?.timeLimit) {
      setTimeRemaining(page.config.timeLimit);
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const handleStart = () => {
    setIsStarted(true);
    setCurrentQuestionIndex(0);
    setSelectedCell(null);
    setAnswers([]);
    setShowResults(false);
    setScore(0);
  };

  const handleTimeout = () => {
    
    setAnswers(prev => {
      const newAnswers = [...prev, {
        questionIndex: currentQuestionIndex,
        questionId: currentQuestion?.id,
        selectedCellId: null,
        isCorrect: false
      }];
      
      if (currentQuestionIndex >= questions.length - 1) {
        const correctCount = newAnswers.filter(a => a.isCorrect).length;
        const calculatedScore = Math.round((correctCount / questions.length) * 100);
        setScore(calculatedScore);
        setIsStarted(false);
        setShowResults(true);
      } else {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
      
      return newAnswers;
    });
  };

  const moveToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setTimeout(() => {
        setAnswers(prev => {
          const correctCount = prev.filter(a => a.isCorrect).length;
          const calculatedScore = Math.round((correctCount / questions.length) * 100);
          setScore(calculatedScore);
          setIsStarted(false);
          setShowResults(true);
          return prev;
        });
      }, 100);
    }
  };

  const handleCellClick = (cell) => {
    if (page.grid.mode === 'familiarization') {
     
      if (cell.audio && audioRefs.current[cell.audio.id]) {
        audioRefs.current[cell.audio.id].play();
      }
    } else if (page.grid.mode === 'quiz' && isStarted) {
     
      if (!selectedCell && timeRemaining > 0 && !isPlaying) {
        setSelectedCell(cell.id);
        
       
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        
        let isCorrect = false;
        
        if (page.correctAnswers && currentQuestion?.audio?.id) {
          const correctAnswer = page.correctAnswers[currentQuestion.audio.id];
          
          if (Array.isArray(correctAnswer)) {
            isCorrect = correctAnswer.includes(cell.id);
          } else {
            isCorrect = correctAnswer === cell.id;
          }
        } else {
          const currentAudioUrl = currentQuestion?.audio?.url;
          const cellAudioUrl = cell.audio?.url;
          const normalizeUrl = (url) => url?.trim().replace(/\/$/, '');
          isCorrect = normalizeUrl(currentAudioUrl) === normalizeUrl(cellAudioUrl);
        }
        
        setAnswers(prev => [...prev, {
          questionIndex: currentQuestionIndex,
          questionId: currentQuestion?.id,
          selectedCellId: cell.id,
          isCorrect: isCorrect
        }]);
        
       
        setTimeout(() => {
          moveToNextQuestion();
        }, 1000); 
      }
    }
  };

  const getCellStyle = (cell) => {
    
    const cellSpecificStyle = cell.style || {};
    const baseStyle = { ...page.grid.cellStyle?.default || {}, ...cellSpecificStyle };
    const isSelected = selectedCell === cell.id;
    
    if (isSelected) {
      let isCorrect = false;
      
      if (page.correctAnswers && currentQuestion?.audio?.id) {
        const correctAnswer = page.correctAnswers[currentQuestion.audio.id];
       
        if (Array.isArray(correctAnswer)) {
          isCorrect = correctAnswer.includes(cell.id);
        } else {
          isCorrect = correctAnswer === cell.id;
        }
      } else {
        const currentAudioUrl = currentQuestion?.audio?.url;
        const cellAudioUrl = cell.audio?.url;
        const normalizeUrl = (url) => url?.trim().replace(/\/$/, '');
        isCorrect = normalizeUrl(currentAudioUrl) === normalizeUrl(cellAudioUrl);
      }
      
      if (isCorrect) {
        return { ...baseStyle, ...page.grid.cellStyle?.correct };
      } else {
        return { ...baseStyle, ...page.grid.cellStyle?.incorrect };
      }
    }
    
   
    if (cell.style?.hover) {
      
    }
    
    return baseStyle;
  };

  const getCellClasses = (cell) => {
    const classes = ['text-center transition-all'];
    
   
    if (cell.className) {
      classes.push(cell.className);
    }
    
    if (page.grid.mode === 'quiz') {
      if (!isStarted || isPlaying) {
        classes.push('cursor-not-allowed opacity-50');
      } else if (selectedCell || timeRemaining === 0) {
        classes.push('cursor-not-allowed opacity-50');
      } else {
        classes.push('cursor-pointer hover:scale-105');
      
        if (cell.style?.hover) {
          
        }
      }
    } else {
      classes.push('cursor-pointer hover:scale-105');
    }
    
    return classes.join(' ');
  };

  return (
    <div>
      {/* Title */}
      {page.title && (
        <h2 className="text-2xl font-bold mb-4 text-gray-800">{page.title}</h2>
      )}

      {/* Instructions */}
      {page.instructions && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-gray-700">{page.instructions.text}</p>
        </div>
      )}

      {/* Start Button (for quiz mode) */}
      {page.grid.mode === 'quiz' && !isStarted && (
        <div className="mb-6 text-center">
          <button
            onClick={handleStart}
            className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
          >
            Start Quiz
          </button>
        </div>
      )}

      {/* Question Progress (for quiz mode) */}
      {page.grid.mode === 'quiz' && isStarted && questions.length > 0 && (
        <div className="mb-4 text-center">
          <span className="text-sm text-gray-600">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
        </div>
      )}

      {/* Timer */}
      {page.config?.showTimer && timeRemaining !== null && (
        <div className="mb-4 text-center">
          <span className={`text-2xl font-bold ${timeRemaining <= 3 ? 'text-red-600' : 'text-gray-700'}`}>
            {timeRemaining}s
          </span>
        </div>
      )}

      {/* Grid */}
      <div
        className="grid mb-6 mx-auto"
        style={{
          gridTemplateColumns: `repeat(${page.grid.columns}, 1fr)`,
          gap: '0px',
          
          width: `${Math.min(page.grid.columns * 75, 450)}px`,
          maxWidth: '100%',
        }}
      >
        {orderedCells.map((cell, displayIndex) => {
          const style = getCellStyle(cell);
          const isSelected = selectedCell === cell.id;
          
          
          const hoverStyle = cell.style?.hover && !isSelected && !selectedCell && timeRemaining > 0 && !isPlaying
            ? cell.style.hover
            : {};
          
          
          const row = Math.floor(displayIndex / page.grid.columns);
          const col = displayIndex % page.grid.columns;
          const isFirstRow = row === 0;
          const isLastRow = row === Math.floor((orderedCells.length - 1) / page.grid.columns);
          const isFirstCol = col === 0;
          const isLastCol = col === page.grid.columns - 1;
          
          return (
            <div key={cell.id} style={{ margin: 0, padding: 0 }}>
              <button
                onClick={() => handleCellClick(cell)}
                disabled={page.grid.mode === 'quiz' && (!isStarted || isPlaying || selectedCell || timeRemaining === 0)}
                className={getCellClasses(cell)}
                style={{
                  backgroundColor: style.backgroundColor || cell.style?.backgroundColor || '#E2F2FF',
                  color: style.textColor || cell.style?.textColor || '#1A588B',
                  border: 'none',
                  borderTop: isFirstRow ? '1px solid #1A588B' : '0.5px solid #1A588B',
                  borderBottom: isLastRow ? '1px solid #1A588B' : '0.5px solid #1A588B',
                  borderLeft: isFirstCol ? '1px solid #1A588B' : '0.5px solid #1A588B',
                  borderRight: isLastCol ? '1px solid #1A588B' : '0.5px solid #1A588B',
                  borderRadius: '0px',
                  padding: '0.5rem',
                  minHeight: '0', 
                  maxHeight: 'none', 
                  aspectRatio: '1',
                  fontSize: page.config?.cellFontSize || '1rem', 
                  fontWeight: style.fontWeight || cell.style?.fontWeight || 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0',
                  width: '100%',
                  height: '100%',
                  boxSizing: 'border-box',
                  ...(hoverStyle && !isSelected && !selectedCell && timeRemaining > 0 && !isPlaying ? {} : {}), // Don't apply hoverStyle in inline styles
                }}
                onMouseEnter={(e) => {
                  if (cell.style?.hover && !isSelected && !selectedCell && timeRemaining > 0 && !isPlaying) {
                    
                    Object.entries(cell.style.hover).forEach(([key, value]) => {
                      if (!['minHeight', 'maxHeight', 'height', 'width', 'padding', 'fontSize', 'transform'].includes(key)) {
                        e.target.style[key] = value;
                      } else if (key === 'transform') {
                        
                        e.target.style[key] = value;
                      }
                    });
                  }
                }}
                onMouseLeave={(e) => {
                  if (cell.style?.hover) {
                    
                    e.target.style.backgroundColor = style.backgroundColor || cell.style?.backgroundColor || '#E2F2FF';
                    e.target.style.color = style.textColor || cell.style?.textColor || '#1A588B';
                    if (cell.style.hover.borderColor) {
                      e.target.style.borderColor = '#1A588B';
                    }
                    
                    e.target.style.minHeight = '0';
                    e.target.style.maxHeight = 'none';
                    e.target.style.height = 'auto';
                    e.target.style.padding = '0.5rem';
                    e.target.style.fontSize = page.config?.cellFontSize || '1rem';
                  }
                }}
              >
                <div style={{ fontSize: page.config?.cellFontSize || '1rem', fontWeight: 'bold' }}>{cell.displayText}</div>
              </button>
              {cell.audio && (
                <audio
                  ref={el => audioRefs.current[cell.audio.id] = el}
                  src={cell.audio.url}
                  preload="metadata"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Question Audio */}
      {page.grid.mode === 'quiz' && (
        <audio
          ref={questionAudioRef}
          preload="metadata"
        />
      )}

      {/* Results Screen */}
      {showResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h2 className="text-3xl font-bold mb-4 text-center">Quiz Results</h2>
            <div className="text-center mb-6">
              <div className="text-6xl font-bold mb-2" style={{ color: score >= 80 ? '#10b981' : '#ef4444' }}>
                {score}%
              </div>
              <p className="text-xl text-gray-700">
                {answers.filter(a => a.isCorrect).length} out of {questions.length} correct
              </p>
              <p className={`text-lg font-semibold mt-2 ${score >= 80 ? 'text-green-600' : 'text-red-600'}`}>
                {score >= 80 ? 'Passed!' : 'Not Passed'}
              </p>
            </div>
            <div className="flex gap-4 justify-center">
              {score < 80 && (
                <button
                  onClick={() => {
                    setShowResults(false);
                    setCurrentQuestionIndex(0);
                    setSelectedCell(null);
                    setAnswers([]);
                    setScore(0);
                    setIsStarted(true);
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  Try Again
                </button>
              )}
              <button
                onClick={() => {
                  if (onNext) {
                    onNext();
                  }
                }}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                {score >= 80 ? 'Continue' : 'Continue Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveGridPage;
